import { type CuisinePreferences, CuisineStance } from '@/types/eatingPreferences'

/**
 * The cuisine vocabulary, in display order. Ids are the wire format — they are stored in Firestore
 * and read by the meal engine, so they never change. American is deliberately absent: it is the
 * base every meal is built on, not a flavor to opt into.
 *
 * Only the seven the engine knows as lanes steer generation today; the rest are stored and shown
 * but do not yet reach the planner.
 */
export enum Cuisine {
  Italian = 'italian',
  Mexican = 'mexican',
  Mediterranean = 'mediterranean',
  Indian = 'indian',
  Chinese = 'chinese',
  Japanese = 'japanese',
  Thai = 'thai',
  Korean = 'korean',
  MiddleEastern = 'middle-eastern',
  Caribbean = 'caribbean'
}

/** Every cuisine in display order — also the tiebreak whenever a set of them has to be listed. */
export const CUISINE_ORDER: Cuisine[] = Object.values(Cuisine)

/** The order a saved palate is read back in. */
export const STANCES: CuisineStance[] = [CuisineStance.Liked, CuisineStance.Disliked]

const isStance = (value: unknown): value is CuisineStance => value === CuisineStance.Liked || value === CuisineStance.Disliked

/** Reads a stored palate, keeping only entries that carry a stance; anything else is no opinion. */
export function normalizeCuisinePreferences(raw: unknown): CuisinePreferences | null {
  if (raw === null || typeof raw !== 'object') return null
  const prefs: CuisinePreferences = {}
  for (const [id, stance] of Object.entries(raw as Record<string, unknown>)) if (isStance(stance)) prefs[id] = stance
  return Object.keys(prefs).length > 0 ? prefs : null
}

/** Cuisine ids at each stance, in display order — a stored map has no order of its own. */
export function groupByStance(prefs: CuisinePreferences | null | undefined): Record<CuisineStance, string[]> {
  const groups: Record<CuisineStance, string[]> = { [CuisineStance.Liked]: [], [CuisineStance.Disliked]: [] }
  if (!prefs) return groups

  const rank = (id: string) => {
    const index = CUISINE_ORDER.indexOf(id as Cuisine)
    return index === -1 ? CUISINE_ORDER.length : index
  }

  for (const [id, stance] of Object.entries(prefs)) if (stance !== undefined) groups[stance].push(id)
  for (const stance of STANCES) groups[stance].sort((a, b) => rank(a) - rank(b) || a.localeCompare(b))
  return groups
}

export const likedCuisines = (prefs: CuisinePreferences | null | undefined): string[] => groupByStance(prefs)[CuisineStance.Liked]

export const dislikedCuisines = (prefs: CuisinePreferences | null | undefined): string[] => groupByStance(prefs)[CuisineStance.Disliked]

/** "middle-eastern" → "Middle Eastern". For the plain-English fallback summary, which isn't i18n'd. */
export const titleCaseCuisineId = (id: string): string =>
  id
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
